import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { SidebarPropertyDynamicPricingComponent } from '@app/modules/calendar/components/sidebar-property-dynamic-pricing/sidebar-property-dynamic-pricing.component';
import { PropertyAvailabilityRulesFormComponent } from '@app/shared/components/property-availability-rules-form/property-availability-rules-form.component';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { UiModule } from '@app/ui';
import { BadgeType } from '@app/ui/badge-mono/badge.component.interface';
import { EventsService } from '@app/shared/services/events/events.service';
import { OnDestroyComponent } from '@app/shared/components/on-destroy/on-destroy.component';
import {
  DynamicPricingOptionsPayload,
  DynamicPricingOptionsResponse,
} from '@app/shared/interfaces/lib/dynamic-pricing.interface';
import { takeUntil } from 'rxjs';

enum View {
  DYNAMIC_PRICING = 'dynamic-pricing',
}

@Component({
  standalone: true,
  selector: 'sbnb-sidebar-property-settings',
  templateUrl: './sidebar-property-settings.component.html',
  styles: `
    :host ::ng-deep .button__secondary {
      padding: 8px 12px !important;
    }

    .button-content {
      font-family: Cera, 'Helvetica Neue', sans-serif;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PropertyAvailabilityRulesFormComponent, CommonModule, UiModule, SidebarPropertyDynamicPricingComponent],
})
export class SidebarPropertySettingsComponent extends OnDestroyComponent implements OnInit {
  public currentView: View | null = null;
  @Input() property: any;
  @Input() dynamicPricingOptions: DynamicPricingOptionsPayload;
  public badgeType = BadgeType;
  public viewEnum = View;

  constructor(
    private eventsService: EventsService,
    private propertiesService: PropertiesService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.eventsService
      .on(`dynamic-pricing-disabled-${this.property.id}`)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.propertiesService.getDynamicPricingOptions(this.property.id).subscribe((data) => {
          this.updateViewData(data as DynamicPricingOptionsResponse);
        });
      });

    this.eventsService
      .on(`dynamic-pricing-enabled-${this.property.id}`)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.propertiesService.getDynamicPricingOptions(this.property.id).subscribe((data) => {
          this.updateViewData(data as DynamicPricingOptionsResponse);
        });
      });

    this.eventsService
      .on(`dynamic-pricing-sidebar-view-force-${this.property.id}`)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((view?: View) => {
        this.setView(view);
      });

    this.eventsService
      .on<boolean>(`dynamic-pricing-visible-change-${this.property.id}`)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((visible) => {
        if (!visible) {
          this.setView();
        }
      });
  }

  updateViewData(data: DynamicPricingOptionsResponse) {
    this.dynamicPricingOptions = data;
    this.setView();
  }

  private setView(view?: View | null) {
    this.currentView = view ?? null;
    this.cdr.detectChanges();
  }
}
